<template>
    <div>
        <table class="w-100 table table_border">
            <thead>
                <tr>
                    <th v-for="column in columns" :class="column.thClass">
                        <div :class="{ 'd-flex align-items-end justify-content-between': column.sortable }">
                            <span><slot :name="'header_' + column.key" :column="column"><span v-html="column.label"></span></slot></span>
                            <div v-if="column.sortable" class="ms-2">
                                <svg
                                    @click="sort(column.key, false)"
                                    xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-triangle-filled" width="10" height="10" viewBox="0 0 24 24" stroke-width="1.5" stroke="#000000" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <path d="M12 1.67a2.914 2.914 0 0 0 -2.492 1.403l-8.11 13.537a2.914 2.914 0 0 0 2.484 4.385h16.225a2.914 2.914 0 0 0 2.503 -4.371l-8.116 -13.546a2.917 2.917 0 0 0 -2.494 -1.408z" stroke-width="0" fill="currentColor" />
                                </svg>
                                <svg
                                    @click="sort(column.key, true)"
                                    xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-triangle-inverted-filled" width="10" height="10" viewBox="0 0 24 24" stroke-width="1.5" stroke="#000000" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <path d="M20.118 3h-16.225a2.914 2.914 0 0 0 -2.503 4.371l8.116 13.549a2.917 2.917 0 0 0 4.987 .005l8.11 -13.539a2.914 2.914 0 0 0 -2.486 -4.386z" stroke-width="0" fill="currentColor" />
                                </svg>
                            </div>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                <template v-for="(item, i) in selectedItems">
                    <tr :id="tableRow(i)">
                        <td v-for="column in columns" :class="column.class"
                        @click="$emit('rowClicked', item)">
                            <slot :name="'data_' + column.key" :item="item">{{ item[column.key] }}</slot>
                        </td>
                        <RightClick :link="createLink(item)" :elementId="tableRow(i)" v-if="link"/>
                    </tr>
                    <tr v-if="item.showDetailRow">
                        <td :colspan="columns.length">
                            <slot name="row_details" :item="item">
                            </slot>
                        </td>
                    </tr>
                </template>
                <tr v-if="selectedItems.length == 0 && !isBusy">
                    <td :colspan="columns.length">
                        <slot name="table-busy">
                            <div class="d-flex align-items-start justify-content-center">
                                <span>{{ $t("table.empty")}}</span>
                            </div>
                        </slot>
                    </td>
                </tr>
                <tr v-if="isBusy">
                    <td :colspan="columns.length">
                        <slot name="table-busy">
                            <div class="d-flex align-items-start justify-content-center">
                                <Loader class="table_loader" :color="'#d8d8d8'"/>
                            </div>
                        </slot>
                    </td>
                </tr>
            </tbody>
        </table>
        <Pagition v-model:actual-page="currentPage" :total-rows="totalRows" v-model:per-page="perPage" :page-options="pageOptions" v-if="pagition"></Pagition>
    </div>
</template>

<script>
import Pagition from '@/components/Pagition.vue';
import Loader from "@/components/Loader.vue";
import RightClick from '@/components/RightClick.vue';
import dataTableMixin from "@/plugins/dataTableMixin.js";

export default {
    props: {
        items: { type: Array, default: () => [], required: false },
        itemsDataProviderSettings: { type: Object, default: () => {}, required: false },
        itemsDataProvider: { type: Function, default: null, required: false },
        filter: { type: Object, default: () => {}, required: false },
        pagition: { type: Boolean, default: true, required: false },
        pageOptions: { type: Array, default: () => [10, 20, 50], required: false },
        columns: { type: Array, default: () => [], required: true},
        sortBy: { type: String, default: "id", required: false },
        sortDesc: { type: Boolean, default: false, required: false },
        link: { type: Function, default: null, required: false },
    },
    components: { Pagition, Loader, RightClick },
    mixins: [dataTableMixin],
    data: function () {
        return {
            selectedItems: [],
            totalRows: 0,
            currentPage: 0,
            perPage: 10,
            isBusy: true,
            tableId: this.tableIdGenerator()
        }
    },
    computed: {
        ctx: function () {
            return {
                perPage: this.perPage,
                currentPage: this.currentPage,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc,
                filter: this.filter
            }
        }
    },
    created: function () {
        this.refresh();
    },
    watch: {
        items: {
            deep: true,
            handler: function (value) {
                this.refresh();
            }
        },
        ctx: {
            deep: true,
            handler: function (value) {
                this.refresh();
            }
        },
        perPage: {
            deep: true,
            handler: function (value) {
                this.refresh();
            }
        },
        currentPage: {
            deep: true,
            handler: function (value) {
                this.refresh();
            }
        },
        filter: {
            deep: true,
            handler: function (value) {
                this.refresh();
            }
        }
    },
    methods: {
        refresh: function () {
            this.selectedItems = [];
            this.isBusy = true;
            if (this.itemsDataProvider != null) {
                this.itemsDataProvider(this.ctx).then((data) => { this.selectedItems = data; this.updateSelectedItems(); this.isBusy = false; });
            }
            else if (this.itemsDataProviderSettings && Object.keys(this.itemsDataProviderSettings).length != 0) {
                this.dataProvider().then((data) => { this.selectedItems = data; this.updateSelectedItems(); this.isBusy = false; });
            }
            else {
                this.selectedItems = this.items;
                this.totalRows = this.items.length;
                this.updateSelectedItems();
                this.isBusy = false;
            }
        },
        updateSelectedItems: function () {
            var data = [];
            for (var i = 0; i < this.selectedItems.length; i++){
                var item = this.selectedItems[i];
                item.showDetailRow = false;
                item.toggleDetails = function () {
                    this.showDetailRow = !this.showDetailRow;
                }
                data.push(item);
            }
            this.selectedItems = data;
        },
        dataProvider: async function () {
            var params = this.initRequestParams(this.ctx);

            var self = this;

            return this.$store.getters.api
                .get(this.itemsDataProviderSettings.url, { params: params, withCredentials: this.itemsDataProviderSettings.credentials })
                .then((response) => {
                    self.totalRows = response.data.count;
                    return response.data.data;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                });
        },
        sort: function (key, desc) {
            this.$emit('update:sortBy', key);
            this.$emit('update:sortDesc', desc);
        },
        setTotalRows: function(rows){
            this.totalRows = rows;
        },
        tableIdGenerator: function () {
            var length = 20
            var result = '';
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            const charactersLength = characters.length;
            var counter = 0;
            while (counter < length) {
                result += characters.charAt(Math.floor(Math.random() * charactersLength));
                counter += 1;
            }
            return result;
        },
        tableRow: function (rowIndex) {
            return 'table-' + this.tableId + '-row-' + rowIndex
        },
        createLink: function (item) {
            var value = this.link(item);
            if (typeof value === 'object') {
                return window.location.origin + this.$router.resolve(value).href;
            }
            return value;
        }
    }
}
</script>

<style lang="scss">
.table_border {
    border: 1px solid #d8d8d8;
    border-radius: 10px !important;
    border-collapse: separate;
    border-spacing: 0px;
    td, th {
        padding: .75rem;
    }

    tr:first-of-type th:first-of-type {
        border-top-left-radius: 10px;
    }
    tr:first-of-type th:last-of-type {
        border-top-right-radius: 10px;
    }
    tr:last-of-type td:first-of-type {
        border-bottom-left-radius: 10px;
    }
    tr:last-of-type td:last-of-type {
        border-bottom-right-radius: 10px;
    }

    tr:hover td{
        background-color: #f8f1ea;
        cursor: pointer;
    }
}
</style>